import type { OrgRole } from '@outpost/api-client';
import { useQuery } from '@tanstack/react-query';

import { useAuth } from './auth';

const RANK: Record<OrgRole, number> = { member: 0, admin: 1, owner: 2 };

/**
 * Whether `role` meets `required`, mirroring `OrgRole::allows` on the server.
 *
 * Only decides which controls to show. The server checks every request itself,
 * so a wrong answer here costs a confusing button, not access.
 */
export function allows(role: OrgRole | undefined, required: OrgRole): boolean {
  return role !== undefined && RANK[role] >= RANK[required];
}

export const ROLES: OrgRole[] = ['member', 'admin', 'owner'];

/**
 * Whether `actor` may change a member holding `current` to `next`. Mirrors
 * `outpost_core::membership::can_change_role`: admins manage members and
 * admins, and only owners grant, revoke or touch the owner role.
 */
export function canChangeRole(actor: OrgRole | undefined, current: OrgRole, next: OrgRole) {
  if (!allows(actor, 'admin')) return false;
  if (current === 'owner' || next === 'owner') return actor === 'owner';
  return true;
}

/** Mirrors `outpost_core::membership::can_invite`: only owners invite owners. */
export function canInvite(actor: OrgRole | undefined, role: OrgRole) {
  return allows(actor, 'admin') && (role !== 'owner' || actor === 'owner');
}

/** Mirrors `outpost_core::membership::can_remove`. Anyone may leave. */
export function canRemove(actor: OrgRole | undefined, target: OrgRole, isSelf: boolean) {
  if (isSelf) return true;
  return allows(actor, 'admin') && (target !== 'owner' || actor === 'owner');
}

/** The caller's organizations, each carrying the caller's role. */
export function useOrganizations() {
  const { client } = useAuth();
  return useQuery({ queryKey: ['organizations'], queryFn: () => client.listOrganizations() });
}

/** The caller's role in `orgId`, or undefined while loading or if not a member. */
export function useOrgRole(orgId: string | undefined): OrgRole | undefined {
  const organizations = useOrganizations();
  return organizations.data?.find((org) => org.id === orgId)?.role;
}
